import Dexie, {} from 'dexie';
import {} from 'fredrick-data-contract';
import {} from 'fredrick-data-contract/claims';
import { parseFredrickDerivedClaimDiagnosticV1, parseFredrickDerivedItemLatestV1, parseFredrickDerivedLineageBranchV1, parseFredrickDerivedListingV1, parseFredrickDerivedSearchPageV1, parseFredrickDerivedSearchSummaryV1, parseFredrickDerivedSearchV1, } from 'fredrick-data-contract/derived';
import { FredrickProtocolParseError, parseFredrickOwlPageCandidateV1, parseFredrickOwlPageRevisionV1, parseFredrickSearchClaimV1, parseFredrickSourceImageV1, } from 'fredrick-data-contract/parse';
import { FREDRICK_MARKET_PROCESSOR_VERSION, FREDRICK_MARKET_STATISTICS_VERSION, calculateFredrickPriceStatistics, fredrickMarketProcessor, normalizeFredrickItemKey, planFredrickImportSession, } from 'fredrick-market-processor';
export const FREDRICK_LOCAL_DATABASE_NAME = 'fredrick-v2';
export const FREDRICK_MARKET_MATERIALIZER_EPOCH = 5;
export class FredrickLocalRepositoryError extends Error {
    code;
    cause;
    constructor(code, message, options) {
        super(message);
        this.name = 'FredrickLocalRepositoryError';
        this.code = code;
        this.cause = options?.cause;
    }
}
class FredrickLocalDatabase extends Dexie {
    captures;
    evidence;
    candidates;
    publishedRevisions;
    revisionSync;
    processingQueue;
    repositoryState;
    syncCursors;
    importSessions;
    importSessionEntries;
    searchClaims;
    searchClaimReceipts;
    searches;
    searchPages;
    listings;
    searchSummaries;
    itemLatest;
    claimDiagnostics;
    lineageBranches;
    constructor(name) {
        super(name);
        this.version(1).stores({
            captures: '&sourceImageId, observedAt, importedAt, localSequence, initialRevisionId',
            evidence: '&evidenceImageId, createdAt',
            candidates: '&revisionId, sourceImageId, evidenceImageId, createdAt',
            publishedRevisions: '&revisionId, sourceImageId, publishedAt',
            revisionSync: '&revisionId, status, updatedAt',
            processingQueue: '&revisionId, requestedAt, sourceImageId',
            repositoryState: '&key',
            syncCursors: '&libraryId, pulledThroughSequence, snapshotGeneration',
            importSessions: '&sessionId, repositoryId, startedAt',
            importSessionEntries: '&id, sessionId, index, sourceImageId, [sessionId+index]',
            searchClaims: '&claimId, repositoryId, searchId, parentClaimId, *sourceImageIds',
            searchClaimReceipts: '&id, sessionId, claimId, createdAt',
            searches: '&searchId, itemKey, marketVariant, observedAtStart, [itemKey+observedAtStart], completeness, groupingState, processorVersion',
            searchPages: '&id, searchId, revisionId, sourceImageId, [searchId+page]',
            listings: '&id, searchId, itemKey, marketVariant, observedAt, [searchId+page+rowIndex], [itemKey+observedAt]',
            searchSummaries: '&searchId, itemKey, marketVariant, observedAt, [itemKey+observedAt], completeness, groupingState',
            itemLatest: '&itemKey, marketVariant, observedAt, searchId',
            claimDiagnostics: '&claimId, repositoryId, searchId, status',
            lineageBranches: '&id, repositoryId, searchId, parentClaimId',
        });
    }
}
const NEXT_LOCAL_SEQUENCE_KEY = 'next-local-sequence';
const REPOSITORY_ID_KEY = 'repository-id';
const PROCESSING_INPUT_GENERATION_KEY = 'processing-input-generation';
const DERIVED_COMMIT_GENERATION_KEY = 'derived-commit-generation';
const COMMITTED_PROCESSOR_VERSION_KEY = 'derived-processor-version';
const COMMITTED_STATISTICS_VERSION_KEY = 'derived-statistics-version';
const COMMITTED_MARKET_VARIANT_KEY = 'derived-market-variant';
const COMMITTED_MATERIALIZER_EPOCH_KEY = 'derived-materializer-epoch';
const REPRESENTED_PROCESSING_INPUT_GENERATION_KEY = 'derived-represented-input-generation';
class StaleSnapshotError extends Error {
}
class NewerMaterializerError extends Error {
}
function randomId(prefix) {
    const value = globalThis.crypto?.randomUUID?.();
    if (value)
        return `${prefix}-${value}`;
    return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
function sessionEntryId(sessionId, index) {
    return JSON.stringify([sessionId, index]);
}
async function firstSessionEntryIndexForEvidence(entries, captures, candidates, sessionId, evidenceImageId) {
    const sessionEntries = (await entries.where('sessionId').equals(sessionId).toArray())
        .sort((left, right) => left.index - right.index);
    const captureRows = await captures.bulkGet(sessionEntries.map((entry) => entry.sourceImageId));
    for (let index = 0; index < sessionEntries.length; index += 1) {
        const entry = sessionEntries[index];
        const capture = captureRows[index];
        if (capture === undefined) {
            throw new Error('An import-session entry references a missing capture.');
        }
        const candidate = await candidates.get(capture.initialRevisionId);
        if (candidate === undefined) {
            throw new Error('An import-session entry references a missing initial candidate.');
        }
        if (candidate.evidenceImageId === evidenceImageId)
            return entry.index;
    }
    return null;
}
function assertEvidenceBlob(value) {
    if (!(value instanceof Blob)) {
        throw new FredrickLocalRepositoryError('invalid-candidate', 'Prepared Owl evidence must be a Blob.');
    }
    if (value.size <= 0) {
        throw new FredrickLocalRepositoryError('invalid-candidate', 'Prepared Owl evidence must not be empty.');
    }
}
function parseCandidate(value) {
    try {
        return parseFredrickOwlPageCandidateV1(value);
    }
    catch (error) {
        throw new FredrickLocalRepositoryError('invalid-candidate', error instanceof Error ? error.message : 'Prepared Owl candidate is invalid.', { cause: error });
    }
}
function isConstraintError(error) {
    if (!(error instanceof Error))
        return false;
    return error.name === 'ConstraintError' || error.name === 'DexieError.ConstraintError';
}
function repositoryError(error, operation) {
    if (error instanceof FredrickLocalRepositoryError)
        return error;
    if (error instanceof FredrickProtocolParseError) {
        return new FredrickLocalRepositoryError('invalid-candidate', error.message, { cause: error });
    }
    if (error instanceof Error && error.name === 'QuotaExceededError') {
        return new FredrickLocalRepositoryError('quota-exceeded', 'The browser did not have enough IndexedDB quota to save this Owl page.', { cause: error });
    }
    if (error instanceof Error
        && (error.name === 'DatabaseClosedError' || error.name === 'InvalidStateError')) {
        return new FredrickLocalRepositoryError('storage-unavailable', 'The local Fredrick repository is unavailable.', { cause: error });
    }
    return new FredrickLocalRepositoryError('transaction-failed', `${operation} failed.`, { cause: error });
}
function validateLimit(value) {
    if (value === undefined)
        return undefined;
    if (!Number.isSafeInteger(value) || value < 1) {
        throw new RangeError('limit must be a positive safe integer.');
    }
    return value;
}
function validateHistoryCompleteness(value) {
    const completeness = value ?? 'complete';
    if (completeness !== 'all' && completeness !== 'complete' && completeness !== 'partial') {
        throw new RangeError('completeness must be all, complete, or partial.');
    }
    return completeness;
}
function compareText(left, right) {
    return left < right ? -1 : left > right ? 1 : 0;
}
function sortedCopy(rows, compare) {
    return [...rows].sort(compare);
}
function errorMessage(error) {
    return error instanceof Error ? error.message : String(error);
}
function buildDebugSessionPlans(sessions, entries, captures, candidates, publishedRevisions, claims) {
    let inputs = null;
    let inputError = null;
    try {
        inputs = buildRevisionInputs(candidates, publishedRevisions);
    }
    catch (error) {
        inputError = errorMessage(error);
    }
    return sortedCopy(sessions, (left, right) => left.startedAt - right.startedAt || compareText(left.sessionId, right.sessionId))
        .map((session) => {
        const sessionEntries = sortedCopy(entries.filter((entry) => entry.sessionId === session.sessionId), (left, right) => left.index - right.index);
        const candidateByRevisionId = new Map(candidates.map((candidate) => [candidate.revisionId, candidate]));
        const evidenceBySource = new Map();
        for (const capture of captures) {
            const candidate = candidateByRevisionId.get(capture.initialRevisionId);
            if (candidate !== undefined) {
                evidenceBySource.set(capture.sourceImageId, candidate.evidenceImageId);
            }
        }
        const firstIndexByEvidence = new Map();
        const debugEntries = sessionEntries.map((entry) => {
            const evidenceImageId = evidenceBySource.get(entry.sourceImageId);
            const duplicateOfIndex = evidenceImageId === undefined
                ? null
                : firstIndexByEvidence.get(evidenceImageId) ?? null;
            if (duplicateOfIndex === null && evidenceImageId !== undefined) {
                firstIndexByEvidence.set(evidenceImageId, entry.index);
            }
            return {
                index: entry.index,
                sourceImageId: entry.sourceImageId,
                duplicateOfIndex,
            };
        });
        if (inputs === null) {
            return {
                session,
                entries: debugEntries,
                proposals: [],
                planningError: inputError ?? 'Could not build revision inputs.',
            };
        }
        try {
            const plan = planFredrickImportSession(session.repositoryId, sessionEntries.map((entry) => entry.sourceImageId), inputs, claims);
            return {
                session,
                entries: debugEntries,
                proposals: plan.proposals,
                planningError: null,
            };
        }
        catch (error) {
            return {
                session,
                entries: debugEntries,
                proposals: [],
                planningError: errorMessage(error),
            };
        }
    });
}
function compareSearchSummaryTuple(left, right) {
    return left.observedAt - right.observedAt
        || left.observedAtEnd - right.observedAtEnd
        || compareText(left.searchId, right.searchId);
}
function applyHistoryFilters(rows, input) {
    const includeAmbiguous = input.includeAmbiguous ?? false;
    const completeness = validateHistoryCompleteness(input.completeness);
    let filtered = [...rows];
    if (!includeAmbiguous) {
        filtered = filtered.filter((row) => row.groupingState === 'high-confidence');
    }
    if (completeness !== 'all') {
        filtered = filtered.filter((row) => row.completeness === completeness);
    }
    filtered.sort(compareSearchSummaryTuple);
    if (input.newestFirst ?? true)
        filtered.reverse();
    const limit = validateLimit(input.limit);
    if (limit !== undefined)
        filtered = filtered.slice(0, limit);
    return filtered;
}
function buildMarketItemSummary(itemKey, summaries, latestSummary, trustedLatest) {
    const observedSpellings = [...new Set(summaries.map((row) => row.itemName))].sort(compareText);
    return {
        itemKey,
        itemName: latestSummary.itemName,
        observedSpellings,
        latestSummary,
        trustedLatest,
        searchCount: summaries.length,
        completeSearchCount: summaries.filter((row) => row.completeness === 'complete').length,
        partialSearchCount: summaries.filter((row) => row.completeness === 'partial').length,
        ambiguousSearchCount: summaries.filter((row) => row.groupingState === 'ambiguous').length,
    };
}
function stateNumber(rows, key, fallback = 0) {
    const value = rows.get(key);
    if (value === undefined)
        return fallback;
    if (!Number.isSafeInteger(value) || value < 0) {
        throw new Error(`Repository state ${key} is invalid.`);
    }
    return value;
}
function stateString(rows, key) {
    const value = rows.get(key);
    if (value === undefined)
        return undefined;
    if (typeof value !== 'string' || value.length === 0) {
        throw new Error(`Repository state ${key} is invalid.`);
    }
    return value;
}
function rowsToStateMap(rows) {
    return new Map(rows.map((row) => [row.key, row.value]));
}
function buildRevisionInputs(candidateRows, publishedRows) {
    const inputs = [];
    const sourceByRevisionId = new Map();
    for (const row of candidateRows) {
        const revision = parseFredrickOwlPageCandidateV1(row.candidate);
        const sourceImageId = revision.capture.sourceImage.sourceImageId;
        if (row.sourceImageId !== sourceImageId
            || row.evidenceImageId !== revision.evidence.evidenceImageId) {
            throw new Error('A candidate wrapper does not match its immutable revision.');
        }
        sourceByRevisionId.set(revision.revisionId, sourceImageId);
        inputs.push({ revision });
    }
    for (const row of publishedRows) {
        const revision = parseFredrickOwlPageRevisionV1(row.revision);
        const sourceImageId = revision.capture.sourceImage.sourceImageId;
        if (row.sourceImageId !== sourceImageId) {
            throw new Error('A published-revision wrapper does not match its immutable revision.');
        }
        const knownSource = sourceByRevisionId.get(revision.revisionId);
        if (knownSource !== undefined && knownSource !== sourceImageId) {
            throw new Error('Candidate and published forms disagree on revision source identity.');
        }
        sourceByRevisionId.set(revision.revisionId, sourceImageId);
        inputs.push({ revision });
    }
    return inputs;
}
function validateProcessorResult(result, processorVersion, statisticsVersion, marketVariant) {
    try {
        const parsed = {
            ...result,
            searches: result.searches.map(parseFredrickDerivedSearchV1),
            pages: result.pages.map(parseFredrickDerivedSearchPageV1),
            listings: result.listings.map(parseFredrickDerivedListingV1),
            summaries: result.summaries.map(parseFredrickDerivedSearchSummaryV1),
            latestItems: result.latestItems.map(parseFredrickDerivedItemLatestV1),
            claimDiagnostics: result.claimDiagnostics.map(parseFredrickDerivedClaimDiagnosticV1),
            lineageBranches: result.lineageBranches.map(parseFredrickDerivedLineageBranchV1),
        };
        if (parsed.searches.some((row) => row.processorVersion !== processorVersion || row.marketVariant !== marketVariant)
            || parsed.summaries.some((row) => row.processorVersion !== processorVersion
                || row.statisticsVersion !== statisticsVersion
                || row.marketVariant !== marketVariant)
            || parsed.latestItems.some((row) => row.processorVersion !== processorVersion
                || row.statisticsVersion !== statisticsVersion
                || row.marketVariant !== marketVariant)) {
            throw new TypeError('Derived records do not match the declared processor versions.');
        }
        const searchById = new Map(parsed.searches.map((row) => [row.searchId, row]));
        const summaryById = new Map(parsed.summaries.map((row) => [row.searchId, row]));
        if (searchById.size !== parsed.searches.length
            || summaryById.size !== parsed.summaries.length
            || searchById.size !== summaryById.size) {
            throw new TypeError('Derived searches and summaries must form a one-to-one set.');
        }
        const pageKeysBySearch = new Map();
        const pagesBySearch = new Map();
        for (const page of parsed.pages) {
            const search = searchById.get(page.searchId);
            if (search === undefined
                || !search.revisionIds.includes(page.revisionId)
                || !search.sourceImageIds.includes(page.sourceImageId)
                || !search.presentPages.includes(page.page)) {
                throw new TypeError('A derived page is inconsistent with its search.');
            }
            const pageKeys = pageKeysBySearch.get(page.searchId) ?? new Set();
            pageKeys.add(JSON.stringify([page.page, page.revisionId, page.sourceImageId]));
            pageKeysBySearch.set(page.searchId, pageKeys);
            const pageNumbers = pagesBySearch.get(page.searchId) ?? new Set();
            pageNumbers.add(page.page);
            pagesBySearch.set(page.searchId, pageNumbers);
        }
        for (const search of parsed.searches) {
            const summary = summaryById.get(search.searchId);
            const pageNumbers = pagesBySearch.get(search.searchId) ?? new Set();
            if (summary === undefined
                || summary.itemName !== search.itemName
                || summary.marketVariant !== search.marketVariant
                || summary.itemKey !== search.itemKey
                || summary.totalResults !== search.totalResults
                || summary.observedAt !== search.observedAtStart
                || summary.observedAtEnd !== search.observedAtEnd
                || summary.completeness !== search.completeness
                || summary.groupingState !== search.groupingState
                || (pageKeysBySearch.get(search.searchId)?.size ?? 0) !== search.revisionIds.length
                || pageNumbers.size !== search.presentPages.length) {
                throw new TypeError('A derived summary or page set is inconsistent with its search.');
            }
        }
        const listingsBySearch = new Map();
        for (const listing of parsed.listings) {
            const search = searchById.get(listing.searchId);
            const pageKey = JSON.stringify([
                listing.page,
                listing.revisionId,
                listing.sourceImageId,
            ]);
            if (search === undefined
                || listing.marketVariant !== search.marketVariant
                || listing.itemKey !== search.itemKey
                || !(pageKeysBySearch.get(listing.searchId)?.has(pageKey) ?? false)) {
                throw new TypeError('A derived listing is inconsistent with its search page.');
            }
            const values = listingsBySearch.get(listing.searchId) ?? [];
            values.push(listing);
            listingsBySearch.set(listing.searchId, values);
        }
        for (const summary of parsed.summaries) {
            const searchListings = listingsBySearch.get(summary.searchId) ?? [];
            let totalUnits = 0;
            for (const listing of searchListings) {
                const next = totalUnits + listing.units;
                if (!Number.isSafeInteger(next)) {
                    throw new TypeError('Derived listed-unit totals exceed the safe integer range.');
                }
                totalUnits = next;
            }
            const prices = calculateFredrickPriceStatistics(searchListings.map((listing) => listing.price));
            if (summary.listingCount !== searchListings.length
                || summary.totalUnits !== totalUnits
                || JSON.stringify(summary.prices) !== JSON.stringify(prices)) {
                throw new TypeError('Derived listing totals or statistics are inconsistent.');
            }
        }
        const latestItemKeys = new Set();
        for (const latest of parsed.latestItems) {
            const summary = summaryById.get(latest.searchId);
            if (latestItemKeys.has(latest.itemKey)) {
                throw new TypeError('Latest-item records must be unique by itemKey.');
            }
            latestItemKeys.add(latest.itemKey);
            if (summary === undefined
                || latest.itemKey !== summary.itemKey
                || latest.itemName !== summary.itemName
                || latest.observedAt !== summary.observedAt
                || latest.observedAtEnd !== summary.observedAtEnd
                || latest.totalResults !== summary.totalResults
                || latest.listingCount !== summary.listingCount
                || latest.totalUnits !== summary.totalUnits
                || latest.marketVariant !== summary.marketVariant
                || latest.completeness !== summary.completeness
                || latest.groupingState !== summary.groupingState
                || JSON.stringify(latest.prices) !== JSON.stringify(summary.prices)) {
                throw new TypeError('A latest-item record is inconsistent with its search summary.');
            }
        }
        return parsed;
    }
    catch (error) {
        throw new FredrickLocalRepositoryError('invalid-derived-record', error instanceof Error ? error.message : 'The market processor produced invalid derived data.', { cause: error });
    }
}
function emptyProcessResult(disposition) {
    return {
        disposition,
        processedQueueCount: 0,
        searchCount: 0,
        listingCount: 0,
        unassignedRevisionIds: [],
        ambiguousRevisionIds: [],
    };
}
export class DexieFredrickLocalRepository {
    processor;
    materializerEpoch;
    databaseName;
    db;
    listeners = new Set();
    processingTail = Promise.resolve();
    constructor(databaseName = FREDRICK_LOCAL_DATABASE_NAME, processor = fredrickMarketProcessor, materializerEpoch = FREDRICK_MARKET_MATERIALIZER_EPOCH) {
        this.processor = processor;
        this.materializerEpoch = materializerEpoch;
        if (!databaseName)
            throw new TypeError('databaseName must be non-empty.');
        if (!Number.isSafeInteger(materializerEpoch) || materializerEpoch < 0) {
            throw new RangeError('materializerEpoch must be a non-negative safe integer.');
        }
        this.databaseName = databaseName;
        this.db = new FredrickLocalDatabase(databaseName);
    }
    async getOrCreateOpenImportSession() {
        let created = false;
        try {
            await this.db.transaction('rw', [this.db.importSessions, this.db.repositoryState], async () => {
                const sessions = await this.db.importSessions.toArray();
                if (sessions.length > 1)
                    throw new Error('More than one import session is open.');
                if (sessions.length === 1)
                    return;
                const repositoryState = await this.db.repositoryState.get(REPOSITORY_ID_KEY);
                const repositoryId = repositoryState === undefined
                    ? randomId('repository')
                    : stateString(new Map([[repositoryState.key, repositoryState.value]]), REPOSITORY_ID_KEY);
                if (repositoryState === undefined) {
                    await this.db.repositoryState.add({ key: REPOSITORY_ID_KEY, value: repositoryId });
                }
                await this.db.importSessions.add({
                    sessionId: randomId('session'),
                    repositoryId,
                    startedAt: Date.now(),
                    nextEntryIndex: 0,
                    generation: 0,
                });
                created = true;
            });
            const view = await this.getOpenImportSession();
            if (view === null)
                throw new Error('The import session could not be created.');
            if (created)
                this.notify();
            return view;
        }
        catch (error) {
            throw repositoryError(error, 'Opening the import session');
        }
    }
    async getOpenImportSession() {
        try {
            const snapshot = await this.db.transaction('r', [
                this.db.importSessions,
                this.db.importSessionEntries,
                this.db.captures,
                this.db.candidates,
                this.db.publishedRevisions,
                this.db.evidence,
                this.db.revisionSync,
                this.db.searchClaims,
            ], async () => {
                const sessions = await this.db.importSessions.toArray();
                if (sessions.length === 0)
                    return null;
                if (sessions.length > 1)
                    throw new Error('More than one import session is open.');
                const session = sessions[0];
                const entries = (await this.db.importSessionEntries.where('sessionId').equals(session.sessionId).toArray())
                    .sort((a, b) => a.index - b.index);
                const captures = await this.db.captures.bulkGet(entries.map((entry) => entry.sourceImageId));
                const revisionIds = captures.map((capture) => {
                    if (capture === undefined)
                        throw new Error('An import-session entry references a missing capture.');
                    return capture.initialRevisionId;
                });
                const candidates = await this.db.candidates.bulkGet(revisionIds);
                const syncRows = await this.db.revisionSync.bulkGet(revisionIds);
                const evidenceIds = candidates.map((candidate) => {
                    if (candidate === undefined)
                        throw new Error('An import-session entry references a missing candidate.');
                    return candidate.evidenceImageId;
                });
                const evidenceRows = await this.db.evidence.bulkGet(evidenceIds);
                const [allCandidates, allPublished, claims] = await Promise.all([
                    this.db.candidates.toArray(),
                    this.db.publishedRevisions.toArray(),
                    this.db.searchClaims.toArray(),
                ]);
                return {
                    session,
                    entries,
                    captures,
                    candidates,
                    syncRows,
                    evidenceRows,
                    inputs: buildRevisionInputs(allCandidates, allPublished),
                    claims: claims.map(parseFredrickSearchClaimV1),
                };
            });
            if (snapshot === null)
                return null;
            const firstIndexByEvidence = new Map();
            const entries = snapshot.entries.map((entry, index) => {
                const capture = snapshot.captures[index];
                const candidate = snapshot.candidates[index];
                const evidence = snapshot.evidenceRows[index];
                const sync = snapshot.syncRows[index];
                if (capture === undefined || candidate === undefined || evidence === undefined || sync === undefined) {
                    throw new Error('An import-session entry references incomplete local records.');
                }
                const duplicateOfIndex = firstIndexByEvidence.get(candidate.evidenceImageId) ?? null;
                if (duplicateOfIndex === null)
                    firstIndexByEvidence.set(candidate.evidenceImageId, entry.index);
                return {
                    index: entry.index,
                    sourceImageId: entry.sourceImageId,
                    duplicateOfIndex,
                    capture,
                    candidate,
                    evidence,
                    sync,
                };
            });
            const plan = planFredrickImportSession(snapshot.session.repositoryId, snapshot.entries.map((entry) => entry.sourceImageId), snapshot.inputs, snapshot.claims);
            return { session: snapshot.session, entries, proposals: plan.proposals };
        }
        catch (error) {
            throw repositoryError(error, 'Reading the import session');
        }
    }
    async appendImportSessionEntry(sessionId, sourceImageId) {
        if (!sessionId)
            throw new TypeError('sessionId must be non-empty.');
        try {
            const result = await this.db.transaction('rw', [this.db.importSessions, this.db.importSessionEntries, this.db.captures, this.db.candidates], async () => {
                const session = await this.db.importSessions.get(sessionId);
                if (session === undefined) {
                    throw new FredrickLocalRepositoryError('invalid-session', 'The import session is no longer open.');
                }
                const capture = await this.db.captures.get(sourceImageId);
                if (capture === undefined) {
                    throw new FredrickLocalRepositoryError('invalid-session', 'The source image is not in the local repository.');
                }
                if (session.nextEntryIndex === Number.MAX_SAFE_INTEGER
                    || session.generation === Number.MAX_SAFE_INTEGER) {
                    throw new Error('The import-session sequence is exhausted.');
                }
                const index = session.nextEntryIndex;
                const candidate = await this.db.candidates.get(capture.initialRevisionId);
                if (candidate === undefined) {
                    throw new Error('The source image is missing its initial candidate.');
                }
                const duplicateOfIndex = await firstSessionEntryIndexForEvidence(this.db.importSessionEntries, this.db.captures, this.db.candidates, sessionId, candidate.evidenceImageId);
                await this.db.importSessionEntries.add({
                    id: sessionEntryId(sessionId, index),
                    sessionId,
                    index,
                    sourceImageId,
                });
                await this.db.importSessions.put({
                    ...session,
                    nextEntryIndex: index + 1,
                    generation: session.generation + 1,
                });
                return {
                    sessionId,
                    sourceImageId,
                    sessionEntryIndex: index,
                    duplicateOfIndex,
                };
            });
            this.notify();
            return result;
        }
        catch (error) {
            throw repositoryError(error, 'Appending to the import session');
        }
    }
    async removeImportSessionEntry(sessionId, index) {
        if (!sessionId)
            throw new TypeError('sessionId must be non-empty.');
        if (!Number.isSafeInteger(index) || index < 0)
            throw new RangeError('index must be a non-negative safe integer.');
        try {
            await this.db.transaction('rw', [this.db.importSessions, this.db.importSessionEntries], async () => {
                const session = await this.db.importSessions.get(sessionId);
                if (session === undefined) {
                    throw new FredrickLocalRepositoryError('invalid-session', 'The import session is no longer open.');
                }
                const id = sessionEntryId(sessionId, index);
                if ((await this.db.importSessionEntries.get(id)) === undefined)
                    return;
                if (session.generation === Number.MAX_SAFE_INTEGER)
                    throw new Error('The import-session generation is exhausted.');
                await this.db.importSessionEntries.delete(id);
                await this.db.importSessions.put({ ...session, generation: session.generation + 1 });
            });
            this.notify();
        }
        catch (error) {
            throw repositoryError(error, 'Removing the import-session entry');
        }
    }
    async discardImportSession(sessionId) {
        if (!sessionId)
            throw new TypeError('sessionId must be non-empty.');
        try {
            await this.db.transaction('rw', [this.db.importSessions, this.db.importSessionEntries], async () => {
                await this.db.importSessionEntries.where('sessionId').equals(sessionId).delete();
                await this.db.importSessions.delete(sessionId);
            });
            this.notify();
        }
        catch (error) {
            throw repositoryError(error, 'Discarding the import session');
        }
    }
    async listSearchClaims() {
        try {
            return (await this.db.searchClaims.toArray())
                .map(parseFredrickSearchClaimV1)
                .sort((a, b) => compareText(a.claimId, b.claimId));
        }
        catch (error) {
            throw repositoryError(error, 'Listing search claims');
        }
    }
    async finishImportSession(sessionId) {
        if (!sessionId)
            throw new TypeError('sessionId must be non-empty.');
        let snapshot;
        try {
            snapshot = await this.readSessionFinishSnapshot(sessionId);
        }
        catch (error) {
            throw repositoryError(error, 'Reading the import-session finish snapshot');
        }
        const plan = planFredrickImportSession(snapshot.session.repositoryId, snapshot.entrySourceImageIds, snapshot.inputs, snapshot.claims);
        try {
            const createdClaimIds = await this.db.transaction('rw', [
                this.db.importSessions,
                this.db.importSessionEntries,
                this.db.searchClaims,
                this.db.searchClaimReceipts,
                this.db.repositoryState,
            ], async () => {
                const currentSession = await this.db.importSessions.get(sessionId);
                if (currentSession === undefined
                    || currentSession.generation !== snapshot.session.generation
                    || currentSession.nextEntryIndex !== snapshot.session.nextEntryIndex) {
                    throw new StaleSnapshotError();
                }
                const currentEntries = (await this.db.importSessionEntries.where('sessionId').equals(sessionId).toArray())
                    .sort((a, b) => a.index - b.index);
                if (JSON.stringify(currentEntries.map((entry) => entry.sourceImageId))
                    !== JSON.stringify(snapshot.entrySourceImageIds)) {
                    throw new StaleSnapshotError();
                }
                const state = await this.db.repositoryState.get(PROCESSING_INPUT_GENERATION_KEY);
                const currentInputGeneration = state === undefined ? 0 : stateNumber(new Map([[state.key, state.value]]), PROCESSING_INPUT_GENERATION_KEY);
                if (currentInputGeneration !== snapshot.processingInputGeneration) {
                    throw new StaleSnapshotError();
                }
                const newClaimIds = [];
                const uniqueClaims = new Map();
                for (const proposal of plan.proposals) {
                    if (proposal.claim !== undefined)
                        uniqueClaims.set(proposal.claim.claimId, proposal.claim);
                }
                for (const claim of uniqueClaims.values()) {
                    const parsed = parseFredrickSearchClaimV1(claim);
                    const existing = await this.db.searchClaims.get(parsed.claimId);
                    if (existing === undefined) {
                        await this.db.searchClaims.add(parsed);
                        newClaimIds.push(parsed.claimId);
                    }
                    else if (JSON.stringify(parseFredrickSearchClaimV1(existing)) !== JSON.stringify(parsed)) {
                        throw new Error('A search claim ID is already bound to a different payload.');
                    }
                }
                const createdAt = Date.now();
                for (const proposal of plan.proposals) {
                    if (proposal.claim === undefined)
                        continue;
                    await this.db.searchClaimReceipts.put({
                        id: JSON.stringify([sessionId, proposal.proposalIndex, proposal.claim.claimId]),
                        sessionId,
                        claimIndex: proposal.proposalIndex,
                        claimId: proposal.claim.claimId,
                        createdAt,
                    });
                }
                await this.db.importSessionEntries.where('sessionId').equals(sessionId).delete();
                await this.db.importSessions.delete(sessionId);
                if (newClaimIds.length > 0) {
                    if (currentInputGeneration === Number.MAX_SAFE_INTEGER) {
                        throw new Error('The processing input generation is exhausted.');
                    }
                    await this.db.repositoryState.put({
                        key: PROCESSING_INPUT_GENERATION_KEY,
                        value: currentInputGeneration + 1,
                    });
                }
                return newClaimIds.sort(compareText);
            });
            this.notify();
            return { sessionId, proposals: plan.proposals, createdClaimIds };
        }
        catch (error) {
            if (error instanceof StaleSnapshotError) {
                throw new FredrickLocalRepositoryError('stale-session', 'The import session changed while it was being finished. Review the refreshed session and finish again.', { cause: error });
            }
            throw repositoryError(error, 'Finishing the import session');
        }
    }
    async importPreparedPage(input) {
        const candidate = parseCandidate(input.candidate);
        assertEvidenceBlob(input.evidenceBlob);
        if (!input.sessionId) {
            throw new FredrickLocalRepositoryError('invalid-session', 'sessionId must be non-empty.');
        }
        const sourceImageId = candidate.capture.sourceImage.sourceImageId;
        const revisionId = candidate.revisionId;
        try {
            const result = await this.db.transaction('rw', [
                this.db.captures,
                this.db.evidence,
                this.db.candidates,
                this.db.revisionSync,
                this.db.processingQueue,
                this.db.repositoryState,
                this.db.importSessions,
                this.db.importSessionEntries,
            ], async () => {
                const session = await this.db.importSessions.get(input.sessionId);
                if (session === undefined) {
                    throw new FredrickLocalRepositoryError('invalid-session', 'The import session is no longer open.');
                }
                if (session.nextEntryIndex === Number.MAX_SAFE_INTEGER
                    || session.generation === Number.MAX_SAFE_INTEGER) {
                    throw new Error('The import-session sequence is exhausted.');
                }
                const sessionEntryIndex = session.nextEntryIndex;
                const existing = await this.db.captures.get(sourceImageId);
                if (existing !== undefined) {
                    const existingCandidate = await this.db.candidates.get(existing.initialRevisionId);
                    if (existingCandidate === undefined) {
                        throw new Error('The source image is missing its initial candidate.');
                    }
                    const duplicateOfIndex = await firstSessionEntryIndexForEvidence(this.db.importSessionEntries, this.db.captures, this.db.candidates, input.sessionId, existingCandidate.evidenceImageId);
                    await this.db.importSessionEntries.add({
                        id: sessionEntryId(input.sessionId, sessionEntryIndex),
                        sessionId: input.sessionId,
                        index: sessionEntryIndex,
                        sourceImageId,
                    });
                    await this.db.importSessions.put({
                        ...session,
                        nextEntryIndex: sessionEntryIndex + 1,
                        generation: session.generation + 1,
                    });
                    return {
                        disposition: 'duplicate',
                        sourceImageId,
                        existingRevisionId: existing.initialRevisionId,
                        sessionEntryIndex,
                        duplicateOfIndex,
                    };
                }
                const stateRows = rowsToStateMap(await this.db.repositoryState.bulkGet([
                    NEXT_LOCAL_SEQUENCE_KEY,
                    PROCESSING_INPUT_GENERATION_KEY,
                ]).then((rows) => rows.filter((row) => row !== undefined)));
                const localSequence = stateNumber(stateRows, NEXT_LOCAL_SEQUENCE_KEY, 1);
                const processingInputGeneration = stateNumber(stateRows, PROCESSING_INPUT_GENERATION_KEY);
                if (localSequence < 1)
                    throw new Error('The local sequence state is invalid.');
                if (processingInputGeneration === Number.MAX_SAFE_INTEGER) {
                    throw new Error('The processing input generation is exhausted.');
                }
                const duplicateOfIndex = await firstSessionEntryIndexForEvidence(this.db.importSessionEntries, this.db.captures, this.db.candidates, input.sessionId, candidate.evidence.evidenceImageId);
                if ((await this.db.evidence.get(candidate.evidence.evidenceImageId)) === undefined) {
                    await this.db.evidence.add({
                        evidenceImageId: candidate.evidence.evidenceImageId,
                        pixelSha256: candidate.evidence.pixelSha256,
                        blob: input.evidenceBlob,
                        createdAt: candidate.capture.importedAt,
                    });
                }
                await this.db.captures.add({
                    sourceImageId,
                    sourceImage: candidate.capture.sourceImage,
                    observedAt: candidate.capture.observedAt,
                    importedAt: candidate.capture.importedAt,
                    localSequence,
                    initialRevisionId: revisionId,
                    ...(input.fileName ? { fileName: input.fileName } : {}),
                });
                await this.db.candidates.add({
                    revisionId,
                    sourceImageId,
                    evidenceImageId: candidate.evidence.evidenceImageId,
                    candidate,
                    createdAt: candidate.transcribedAt,
                });
                await this.db.revisionSync.add({
                    revisionId,
                    status: 'local-only',
                    updatedAt: candidate.capture.importedAt,
                });
                await this.db.processingQueue.add({
                    revisionId,
                    sourceImageId,
                    requestedAt: candidate.capture.importedAt,
                });
                await this.db.importSessionEntries.add({
                    id: sessionEntryId(input.sessionId, sessionEntryIndex),
                    sessionId: input.sessionId,
                    index: sessionEntryIndex,
                    sourceImageId,
                });
                await this.db.importSessions.put({
                    ...session,
                    nextEntryIndex: sessionEntryIndex + 1,
                    generation: session.generation + 1,
                });
                await this.db.repositoryState.bulkPut([
                    { key: NEXT_LOCAL_SEQUENCE_KEY, value: localSequence + 1 },
                    { key: PROCESSING_INPUT_GENERATION_KEY, value: processingInputGeneration + 1 },
                ]);
                return {
                    disposition: 'created',
                    sourceImageId,
                    revisionId,
                    localSequence,
                    sessionEntryIndex,
                    duplicateOfIndex,
                };
            });
            this.notify();
            return result;
        }
        catch (error) {
            if (isConstraintError(error)) {
                const existing = await this.db.captures.get(sourceImageId);
                if (existing !== undefined) {
                    const appended = await this.appendImportSessionEntry(input.sessionId, sourceImageId);
                    return {
                        disposition: 'duplicate',
                        sourceImageId,
                        existingRevisionId: existing.initialRevisionId,
                        sessionEntryIndex: appended.sessionEntryIndex,
                        duplicateOfIndex: appended.duplicateOfIndex,
                    };
                }
            }
            throw repositoryError(error, 'Saving the Owl page');
        }
    }
    async hasSourceImage(sourceImageId) {
        try {
            return (await this.db.captures.get(sourceImageId)) !== undefined;
        }
        catch (error) {
            throw repositoryError(error, 'Checking the local repository');
        }
    }
    async getCapture(sourceImageId) {
        try {
            const value = await this.db.captures.get(sourceImageId);
            if (value === undefined)
                return null;
            parseFredrickSourceImageV1(value.sourceImage);
            return value;
        }
        catch (error) {
            throw repositoryError(error, 'Reading the local capture');
        }
    }
    async getCaptureBundle(sourceImageId) {
        try {
            return await this.db.transaction('r', this.db.captures, this.db.candidates, this.db.revisionSync, async () => {
                const capture = await this.db.captures.get(sourceImageId);
                if (capture === undefined)
                    return null;
                const candidate = await this.db.candidates.get(capture.initialRevisionId);
                const sync = await this.db.revisionSync.get(capture.initialRevisionId);
                if (candidate === undefined || sync === undefined) {
                    throw new Error('The local capture references missing repository records.');
                }
                parseFredrickOwlPageCandidateV1(candidate.candidate);
                return { capture, candidate, sync };
            });
        }
        catch (error) {
            throw repositoryError(error, 'Reading the local capture bundle');
        }
    }
    async getCandidate(revisionId) {
        try {
            const value = await this.db.candidates.get(revisionId);
            if (value === undefined)
                return null;
            parseFredrickOwlPageCandidateV1(value.candidate);
            return value;
        }
        catch (error) {
            throw repositoryError(error, 'Reading the local candidate');
        }
    }
    async getEvidence(evidenceImageId) {
        try {
            return (await this.db.evidence.get(evidenceImageId)) ?? null;
        }
        catch (error) {
            throw repositoryError(error, 'Reading local Owl evidence');
        }
    }
    async getRevisionSyncState(revisionId) {
        try {
            return (await this.db.revisionSync.get(revisionId)) ?? null;
        }
        catch (error) {
            throw repositoryError(error, 'Reading local synchronization state');
        }
    }
    async listCaptures(input = {}) {
        const limit = validateLimit(input.limit);
        const newestFirst = input.newestFirst ?? true;
        try {
            let captures = await this.db.captures.orderBy('localSequence').toArray();
            if (newestFirst)
                captures = captures.reverse();
            if (limit !== undefined)
                captures = captures.slice(0, limit);
            const revisionIds = captures.map((capture) => capture.initialRevisionId);
            const [candidates, states] = await Promise.all([
                this.db.candidates.bulkGet(revisionIds),
                this.db.revisionSync.bulkGet(revisionIds),
            ]);
            return captures.map((capture, index) => {
                const candidateRecord = candidates[index];
                const sync = states[index];
                if (candidateRecord === undefined || sync === undefined) {
                    throw new Error('A local capture references missing repository records.');
                }
                const candidate = parseFredrickOwlPageCandidateV1(candidateRecord.candidate);
                return {
                    sourceImageId: capture.sourceImageId,
                    revisionId: candidate.revisionId,
                    evidenceImageId: candidate.evidence.evidenceImageId,
                    observedAt: capture.observedAt,
                    importedAt: capture.importedAt,
                    localSequence: capture.localSequence,
                    ...(capture.fileName ? { fileName: capture.fileName } : {}),
                    itemName: candidate.transcription.itemName,
                    page: candidate.transcription.page,
                    totalPages: candidate.transcription.totalPages,
                    totalResults: candidate.transcription.totalResults,
                    listingCount: candidate.transcription.listings.length,
                    partial: candidate.partial,
                    syncStatus: sync.status,
                };
            });
        }
        catch (error) {
            throw repositoryError(error, 'Listing local captures');
        }
    }
    async listPendingRevisionIds() {
        try {
            const rows = await this.db.revisionSync.where('status').equals('local-only').toArray();
            return rows.map((row) => row.revisionId);
        }
        catch (error) {
            throw repositoryError(error, 'Listing pending revisions');
        }
    }
    async getSyncCursor(libraryId) {
        if (!libraryId)
            throw new TypeError('libraryId must be non-empty.');
        try {
            return (await this.db.syncCursors.get(libraryId)) ?? null;
        }
        catch (error) {
            throw repositoryError(error, 'Reading the synchronization cursor');
        }
    }
    processPending() {
        return this.serializeProcessing(() => this.runProcessing(false));
    }
    rebuildAllDerived() {
        return this.serializeProcessing(() => this.runProcessing(true));
    }
    async listLatestItems(input = {}) {
        const limit = validateLimit(input.limit);
        const newestFirst = input.newestFirst ?? true;
        try {
            let rows = (await this.db.itemLatest.toArray()).map(parseFredrickDerivedItemLatestV1);
            rows.sort((a, b) => compareSearchSummaryTuple(a, b) || compareText(a.itemKey, b.itemKey));
            if (newestFirst)
                rows.reverse();
            if (limit !== undefined)
                rows = rows.slice(0, limit);
            return rows;
        }
        catch (error) {
            if (error instanceof TypeError) {
                throw new FredrickLocalRepositoryError('invalid-derived-record', error.message, { cause: error });
            }
            throw repositoryError(error, 'Listing latest market items');
        }
    }
    async getMarketOverview() {
        try {
            return await this.db.transaction('r', [this.db.captures, this.db.processingQueue, this.db.searchSummaries, this.db.itemLatest], async () => {
                const [captureCount, pendingProcessingCount, summaryValues, latestValues] = await Promise.all([
                    this.db.captures.count(),
                    this.db.processingQueue.count(),
                    this.db.searchSummaries.toArray(),
                    this.db.itemLatest.toArray(),
                ]);
                const summaries = summaryValues.map(parseFredrickDerivedSearchSummaryV1);
                latestValues.forEach(parseFredrickDerivedItemLatestV1);
                return {
                    captureCount,
                    pendingProcessingCount,
                    searchCount: summaries.length,
                    completeSearchCount: summaries.filter((row) => row.completeness === 'complete').length,
                    partialSearchCount: summaries.filter((row) => row.completeness === 'partial').length,
                    ambiguousSearchCount: summaries.filter((row) => row.groupingState === 'ambiguous').length,
                    trustedLatestItemCount: latestValues.length,
                };
            });
        }
        catch (error) {
            if (error instanceof TypeError) {
                throw new FredrickLocalRepositoryError('invalid-derived-record', error.message, { cause: error });
            }
            throw repositoryError(error, 'Reading the local market overview');
        }
    }
    async listMarketItems(input = {}) {
        const limit = validateLimit(input.limit);
        const includePartial = input.includePartial ?? false;
        const includeAmbiguous = input.includeAmbiguous ?? false;
        const newestFirst = input.newestFirst ?? true;
        const rawQuery = input.query?.trim() ?? '';
        const normalizedQuery = rawQuery.length === 0 ? '' : normalizeFredrickItemKey(rawQuery);
        try {
            return await this.db.transaction('r', [this.db.searchSummaries, this.db.itemLatest], async () => {
                const [summaryValues, latestValues] = await Promise.all([
                    this.db.searchSummaries.toArray(),
                    this.db.itemLatest.toArray(),
                ]);
                const summaries = summaryValues.map(parseFredrickDerivedSearchSummaryV1);
                const latestByItem = new Map(latestValues.map((value) => {
                    const parsed = parseFredrickDerivedItemLatestV1(value);
                    return [parsed.itemKey, parsed];
                }));
                const grouped = new Map();
                for (const summary of summaries) {
                    const rows = grouped.get(summary.itemKey);
                    if (rows === undefined)
                        grouped.set(summary.itemKey, [summary]);
                    else
                        rows.push(summary);
                }
                let items = [];
                for (const [itemKey, all] of grouped) {
                    if (normalizedQuery.length > 0 && !itemKey.includes(normalizedQuery))
                        continue;
                    const eligible = all.filter((row) => (includePartial || row.completeness === 'complete')
                        && (includeAmbiguous || row.groupingState === 'high-confidence'));
                    if (eligible.length === 0)
                        continue;
                    const latestSummary = [...eligible].sort(compareSearchSummaryTuple).at(-1);
                    items.push(buildMarketItemSummary(itemKey, all, latestSummary, latestByItem.get(itemKey) ?? null));
                }
                items.sort((a, b) => compareSearchSummaryTuple(a.latestSummary, b.latestSummary)
                    || compareText(a.itemKey, b.itemKey));
                if (newestFirst)
                    items.reverse();
                if (limit !== undefined)
                    items = items.slice(0, limit);
                return items;
            });
        }
        catch (error) {
            if (error instanceof TypeError) {
                throw new FredrickLocalRepositoryError('invalid-derived-record', error.message, { cause: error });
            }
            throw repositoryError(error, 'Listing local market items');
        }
    }
    async getMarketItem(itemKey) {
        if (!itemKey)
            throw new TypeError('itemKey must be non-empty.');
        try {
            return await this.db.transaction('r', [this.db.searchSummaries, this.db.itemLatest], async () => {
                const [summaryValues, latestValue] = await Promise.all([
                    this.db.searchSummaries.where('itemKey').equals(itemKey).toArray(),
                    this.db.itemLatest.get(itemKey),
                ]);
                const summaries = summaryValues.map(parseFredrickDerivedSearchSummaryV1);
                if (summaries.length === 0)
                    return null;
                const latestSummary = [...summaries].sort(compareSearchSummaryTuple).at(-1);
                const trustedLatest = latestValue === undefined
                    ? null
                    : parseFredrickDerivedItemLatestV1(latestValue);
                return buildMarketItemSummary(itemKey, summaries, latestSummary, trustedLatest);
            });
        }
        catch (error) {
            if (error instanceof TypeError) {
                throw new FredrickLocalRepositoryError('invalid-derived-record', error.message, { cause: error });
            }
            throw repositoryError(error, 'Reading the local market item');
        }
    }
    async listSearchHistory(itemKey, input = {}) {
        if (!itemKey)
            throw new TypeError('itemKey must be non-empty.');
        validateHistoryCompleteness(input.completeness);
        validateLimit(input.limit);
        try {
            const rows = (await this.db.searchSummaries.where('itemKey').equals(itemKey).toArray())
                .map(parseFredrickDerivedSearchSummaryV1);
            return applyHistoryFilters(rows, input);
        }
        catch (error) {
            if (error instanceof TypeError) {
                throw new FredrickLocalRepositoryError('invalid-derived-record', error.message, { cause: error });
            }
            throw repositoryError(error, 'Listing market search history');
        }
    }
    async listSearchHistoryEntries(itemKey, input = {}) {
        if (!itemKey)
            throw new TypeError('itemKey must be non-empty.');
        validateHistoryCompleteness(input.completeness);
        validateLimit(input.limit);
        try {
            return await this.db.transaction('r', [this.db.searchSummaries, this.db.searches], async () => {
                const summaryValues = await this.db.searchSummaries.where('itemKey').equals(itemKey).toArray();
                const summaries = summaryValues.map(parseFredrickDerivedSearchSummaryV1);
                const filtered = applyHistoryFilters(summaries, input);
                const searchValues = await this.db.searches.bulkGet(filtered.map((row) => row.searchId));
                return filtered.map((summary, index) => {
                    const value = searchValues[index];
                    if (value === undefined) {
                        throw new TypeError('The derived history summary is missing its search record.');
                    }
                    const search = parseFredrickDerivedSearchV1(value);
                    if (search.itemKey !== itemKey || search.searchId !== summary.searchId) {
                        throw new TypeError('The derived history search is inconsistent with its summary.');
                    }
                    return {
                        summary,
                        presentPages: search.presentPages,
                        expectedPageCount: search.expectedPageCount,
                    };
                });
            });
        }
        catch (error) {
            if (error instanceof TypeError) {
                throw new FredrickLocalRepositoryError('invalid-derived-record', error.message, { cause: error });
            }
            throw repositoryError(error, 'Listing market search history entries');
        }
    }
    async getSearchDetail(searchId) {
        if (!searchId)
            throw new TypeError('searchId must be non-empty.');
        try {
            return await this.db.transaction('r', [this.db.searches, this.db.searchSummaries, this.db.searchPages, this.db.listings], async () => {
                const [searchValue, summaryValue, pageValues, listingValues] = await Promise.all([
                    this.db.searches.get(searchId),
                    this.db.searchSummaries.get(searchId),
                    this.db.searchPages.where('searchId').equals(searchId).toArray(),
                    this.db.listings.where('searchId').equals(searchId).toArray(),
                ]);
                if (searchValue === undefined && summaryValue === undefined)
                    return null;
                if (searchValue === undefined || summaryValue === undefined) {
                    throw new TypeError('The derived search is missing its search or summary record.');
                }
                const pages = pageValues.map(parseFredrickDerivedSearchPageV1)
                    .sort((a, b) => a.page - b.page
                    || (a.revisionId < b.revisionId ? -1 : a.revisionId > b.revisionId ? 1 : 0));
                const listings = listingValues.map(parseFredrickDerivedListingV1)
                    .sort((a, b) => a.page - b.page
                    || a.rowIndex - b.rowIndex
                    || (a.revisionId < b.revisionId ? -1 : a.revisionId > b.revisionId ? 1 : 0));
                return {
                    search: parseFredrickDerivedSearchV1(searchValue),
                    summary: parseFredrickDerivedSearchSummaryV1(summaryValue),
                    pages,
                    listings,
                };
            });
        }
        catch (error) {
            if (error instanceof TypeError) {
                throw new FredrickLocalRepositoryError('invalid-derived-record', error.message, { cause: error });
            }
            throw repositoryError(error, 'Reading market search detail');
        }
    }
    async exportDebugData() {
        try {
            const exportedAt = Date.now();
            const snapshot = await this.db.transaction('r', [
                this.db.captures,
                this.db.evidence,
                this.db.candidates,
                this.db.publishedRevisions,
                this.db.revisionSync,
                this.db.processingQueue,
                this.db.repositoryState,
                this.db.syncCursors,
                this.db.importSessions,
                this.db.importSessionEntries,
                this.db.searchClaims,
                this.db.searchClaimReceipts,
                this.db.searches,
                this.db.searchPages,
                this.db.listings,
                this.db.searchSummaries,
                this.db.itemLatest,
                this.db.claimDiagnostics,
                this.db.lineageBranches,
            ], async () => {
                const [captures, evidenceImageCount, candidates, publishedRevisions, revisionSync, processingQueue, repositoryState, syncCursors, importSessions, importSessionEntries, searchClaims, searchClaimReceipts, searches, searchPages, listings, searchSummaries, itemLatest, claimDiagnostics, lineageBranches,] = await Promise.all([
                    this.db.captures.toArray(),
                    this.db.evidence.count(),
                    this.db.candidates.toArray(),
                    this.db.publishedRevisions.toArray(),
                    this.db.revisionSync.toArray(),
                    this.db.processingQueue.toArray(),
                    this.db.repositoryState.toArray(),
                    this.db.syncCursors.toArray(),
                    this.db.importSessions.toArray(),
                    this.db.importSessionEntries.toArray(),
                    this.db.searchClaims.toArray(),
                    this.db.searchClaimReceipts.toArray(),
                    this.db.searches.toArray(),
                    this.db.searchPages.toArray(),
                    this.db.listings.toArray(),
                    this.db.searchSummaries.toArray(),
                    this.db.itemLatest.toArray(),
                    this.db.claimDiagnostics.toArray(),
                    this.db.lineageBranches.toArray(),
                ]);
                return {
                    evidenceImageCount,
                    captures,
                    candidates,
                    publishedRevisions,
                    revisionSync,
                    processingQueue,
                    repositoryState,
                    syncCursors,
                    importSessions,
                    importSessionEntries,
                    searchClaims,
                    searchClaimReceipts,
                    searches,
                    searchPages,
                    listings,
                    searchSummaries,
                    itemLatest,
                    claimDiagnostics,
                    lineageBranches,
                };
            });
            const tables = {
                captures: sortedCopy(snapshot.captures, (left, right) => left.localSequence - right.localSequence || compareText(left.sourceImageId, right.sourceImageId)),
                candidates: sortedCopy(snapshot.candidates, (left, right) => left.createdAt - right.createdAt || compareText(left.revisionId, right.revisionId)),
                publishedRevisions: sortedCopy(snapshot.publishedRevisions, (left, right) => left.publishedAt - right.publishedAt || compareText(left.revisionId, right.revisionId)),
                revisionSync: sortedCopy(snapshot.revisionSync, (left, right) => compareText(left.revisionId, right.revisionId)),
                processingQueue: sortedCopy(snapshot.processingQueue, (left, right) => left.requestedAt - right.requestedAt || compareText(left.revisionId, right.revisionId)),
                repositoryState: sortedCopy(snapshot.repositoryState, (left, right) => compareText(left.key, right.key)),
                syncCursors: sortedCopy(snapshot.syncCursors, (left, right) => compareText(left.libraryId, right.libraryId)),
                importSessions: sortedCopy(snapshot.importSessions, (left, right) => left.startedAt - right.startedAt || compareText(left.sessionId, right.sessionId)),
                importSessionEntries: sortedCopy(snapshot.importSessionEntries, (left, right) => compareText(left.sessionId, right.sessionId) || left.index - right.index),
                searchClaims: sortedCopy(snapshot.searchClaims, (left, right) => compareText(left.claimId, right.claimId)),
                searchClaimReceipts: sortedCopy(snapshot.searchClaimReceipts, (left, right) => compareText(left.sessionId, right.sessionId)
                    || left.claimIndex - right.claimIndex
                    || compareText(left.claimId, right.claimId)),
                searches: sortedCopy(snapshot.searches, (left, right) => compareText(left.searchId, right.searchId)),
                searchPages: sortedCopy(snapshot.searchPages, (left, right) => compareText(left.searchId, right.searchId)
                    || left.page - right.page
                    || compareText(left.revisionId, right.revisionId)),
                listings: sortedCopy(snapshot.listings, (left, right) => compareText(left.searchId, right.searchId)
                    || left.page - right.page
                    || left.rowIndex - right.rowIndex
                    || compareText(left.revisionId, right.revisionId)),
                searchSummaries: sortedCopy(snapshot.searchSummaries, (left, right) => compareText(left.searchId, right.searchId)),
                itemLatest: sortedCopy(snapshot.itemLatest, (left, right) => compareText(left.itemKey, right.itemKey)),
                claimDiagnostics: sortedCopy(snapshot.claimDiagnostics, (left, right) => compareText(left.claimId, right.claimId)),
                lineageBranches: sortedCopy(snapshot.lineageBranches, (left, right) => compareText(left.id, right.id)),
            };
            return {
                format: 'fredrick-local-debug-export',
                schemaVersion: 1,
                exportedAt,
                database: {
                    name: this.databaseName,
                    schemaVersion: this.db.verno,
                    materializerEpoch: this.materializerEpoch,
                    processorVersion: this.processor.version,
                    statisticsVersion: this.processor.statisticsVersion,
                    marketVariant: this.processor.marketVariant,
                },
                privacy: {
                    imageBytesIncluded: false,
                    omittedEvidenceImageCount: snapshot.evidenceImageCount,
                    omittedTables: ['evidence'],
                    note: 'Private evidence rows and all Blob/image bytes are omitted. Evidence IDs and pixel hashes remain available through candidate records.',
                },
                counts: {
                    captures: tables.captures.length,
                    evidenceImagesOmitted: snapshot.evidenceImageCount,
                    candidates: tables.candidates.length,
                    publishedRevisions: tables.publishedRevisions.length,
                    revisionSync: tables.revisionSync.length,
                    processingQueue: tables.processingQueue.length,
                    repositoryState: tables.repositoryState.length,
                    syncCursors: tables.syncCursors.length,
                    importSessions: tables.importSessions.length,
                    importSessionEntries: tables.importSessionEntries.length,
                    searchClaims: tables.searchClaims.length,
                    searchClaimReceipts: tables.searchClaimReceipts.length,
                    searches: tables.searches.length,
                    searchPages: tables.searchPages.length,
                    listings: tables.listings.length,
                    searchSummaries: tables.searchSummaries.length,
                    itemLatest: tables.itemLatest.length,
                    claimDiagnostics: tables.claimDiagnostics.length,
                    lineageBranches: tables.lineageBranches.length,
                },
                importSessionPlans: buildDebugSessionPlans(tables.importSessions, tables.importSessionEntries, tables.captures, tables.candidates, tables.publishedRevisions, tables.searchClaims),
                tables,
            };
        }
        catch (error) {
            throw repositoryError(error, 'Exporting local debug data');
        }
    }
    async deleteAllLocalData() {
        try {
            await this.db.transaction('rw', [
                this.db.captures,
                this.db.evidence,
                this.db.candidates,
                this.db.publishedRevisions,
                this.db.revisionSync,
                this.db.processingQueue,
                this.db.repositoryState,
                this.db.syncCursors,
                this.db.importSessions,
                this.db.importSessionEntries,
                this.db.searchClaims,
                this.db.searchClaimReceipts,
                this.db.searches,
                this.db.searchPages,
                this.db.listings,
                this.db.searchSummaries,
                this.db.itemLatest,
                this.db.claimDiagnostics,
                this.db.lineageBranches,
            ], async () => {
                const states = rowsToStateMap(await this.db.repositoryState.bulkGet([
                    PROCESSING_INPUT_GENERATION_KEY,
                    DERIVED_COMMIT_GENERATION_KEY,
                ]).then((rows) => rows.filter((row) => row !== undefined)));
                const inputGeneration = stateNumber(states, PROCESSING_INPUT_GENERATION_KEY);
                const derivedGeneration = stateNumber(states, DERIVED_COMMIT_GENERATION_KEY);
                if (inputGeneration === Number.MAX_SAFE_INTEGER
                    || derivedGeneration === Number.MAX_SAFE_INTEGER) {
                    throw new Error('The repository generation is exhausted.');
                }
                await Promise.all([
                    this.db.captures.clear(),
                    this.db.evidence.clear(),
                    this.db.candidates.clear(),
                    this.db.publishedRevisions.clear(),
                    this.db.revisionSync.clear(),
                    this.db.processingQueue.clear(),
                    this.db.syncCursors.clear(),
                    this.db.importSessions.clear(),
                    this.db.importSessionEntries.clear(),
                    this.db.searchClaims.clear(),
                    this.db.searchClaimReceipts.clear(),
                    this.db.searches.clear(),
                    this.db.searchPages.clear(),
                    this.db.listings.clear(),
                    this.db.searchSummaries.clear(),
                    this.db.itemLatest.clear(),
                    this.db.claimDiagnostics.clear(),
                    this.db.lineageBranches.clear(),
                ]);
                await this.db.repositoryState.clear();
                // Preserve monotonic tombstones so a pre-clear snapshot cannot match
                // again after all other state has been reset (the generation-zero ABA).
                await this.db.repositoryState.bulkPut([
                    { key: PROCESSING_INPUT_GENERATION_KEY, value: inputGeneration + 1 },
                    { key: DERIVED_COMMIT_GENERATION_KEY, value: derivedGeneration + 1 },
                ]);
            });
            this.notify();
        }
        catch (error) {
            throw repositoryError(error, 'Clearing the local repository');
        }
    }
    subscribe(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }
    close() {
        this.db.close();
    }
    async deleteDatabase() {
        this.db.close();
        await Dexie.delete(this.databaseName);
        this.notify();
    }
    async readSessionFinishSnapshot(sessionId) {
        return this.db.transaction('r', [
            this.db.importSessions,
            this.db.importSessionEntries,
            this.db.candidates,
            this.db.publishedRevisions,
            this.db.searchClaims,
            this.db.repositoryState,
        ], async () => {
            const session = await this.db.importSessions.get(sessionId);
            if (session === undefined) {
                throw new FredrickLocalRepositoryError('invalid-session', 'The import session is no longer open.');
            }
            const [entries, candidateRows, publishedRows, claimRows, state] = await Promise.all([
                this.db.importSessionEntries.where('sessionId').equals(sessionId).toArray(),
                this.db.candidates.toArray(),
                this.db.publishedRevisions.toArray(),
                this.db.searchClaims.toArray(),
                this.db.repositoryState.get(PROCESSING_INPUT_GENERATION_KEY),
            ]);
            entries.sort((a, b) => a.index - b.index);
            return {
                session,
                entrySourceImageIds: entries.map((entry) => entry.sourceImageId),
                inputs: buildRevisionInputs(candidateRows, publishedRows),
                claims: claimRows.map(parseFredrickSearchClaimV1),
                processingInputGeneration: state === undefined
                    ? 0
                    : stateNumber(new Map([[state.key, state.value]]), PROCESSING_INPUT_GENERATION_KEY),
            };
        });
    }
    async readProcessingSnapshot() {
        return this.db.transaction('r', [
            this.db.candidates,
            this.db.publishedRevisions,
            this.db.processingQueue,
            this.db.searchClaims,
            this.db.repositoryState,
        ], async () => {
            const [candidateRows, publishedRows, queueRows, claimRows, stateRows] = await Promise.all([
                this.db.candidates.toArray(),
                this.db.publishedRevisions.toArray(),
                this.db.processingQueue.toArray(),
                this.db.searchClaims.toArray(),
                this.db.repositoryState.toArray(),
            ]);
            const inputs = buildRevisionInputs(candidateRows, publishedRows);
            const sourceByRevisionId = new Map();
            for (const input of inputs) {
                sourceByRevisionId.set(input.revision.revisionId, input.revision.capture.sourceImage.sourceImageId);
            }
            for (const queueRow of queueRows) {
                const sourceImageId = sourceByRevisionId.get(queueRow.revisionId);
                if (sourceImageId === undefined || sourceImageId !== queueRow.sourceImageId) {
                    throw new Error('A processing-queue row references missing or mismatched input.');
                }
            }
            const states = rowsToStateMap(stateRows);
            return {
                inputs,
                claims: claimRows.map(parseFredrickSearchClaimV1),
                queueKeys: queueRows.map((row) => row.revisionId).sort(compareText),
                processingInputGeneration: stateNumber(states, PROCESSING_INPUT_GENERATION_KEY),
                representedProcessingInputGeneration: stateNumber(states, REPRESENTED_PROCESSING_INPUT_GENERATION_KEY),
                derivedCommitGeneration: stateNumber(states, DERIVED_COMMIT_GENERATION_KEY),
                committedProcessorVersion: stateString(states, COMMITTED_PROCESSOR_VERSION_KEY),
                committedStatisticsVersion: stateString(states, COMMITTED_STATISTICS_VERSION_KEY),
                committedMarketVariant: stateString(states, COMMITTED_MARKET_VARIANT_KEY),
                committedMaterializerEpoch: stateNumber(states, COMMITTED_MATERIALIZER_EPOCH_KEY),
            };
        });
    }
    async runProcessing(force) {
        let snapshot;
        try {
            snapshot = await this.readProcessingSnapshot();
        }
        catch (error) {
            throw repositoryError(error, 'Reading the market-processing snapshot');
        }
        if (this.materializerEpoch < snapshot.committedMaterializerEpoch) {
            return emptyProcessResult('newer-materializer');
        }
        const requiresRebuild = force
            || snapshot.queueKeys.length > 0
            || snapshot.representedProcessingInputGeneration !== snapshot.processingInputGeneration
            || snapshot.committedProcessorVersion !== this.processor.version
            || snapshot.committedStatisticsVersion !== this.processor.statisticsVersion
            || snapshot.committedMarketVariant !== this.processor.marketVariant
            || this.materializerEpoch > snapshot.committedMaterializerEpoch;
        if (!requiresRebuild)
            return emptyProcessResult('no-work');
        let processed;
        try {
            processed = validateProcessorResult(this.processor.process(snapshot.inputs, snapshot.claims), this.processor.version, this.processor.statisticsVersion, this.processor.marketVariant);
        }
        catch (error) {
            throw repositoryError(error, 'Processing local market data');
        }
        try {
            await this.db.transaction('rw', [
                this.db.searches,
                this.db.searchPages,
                this.db.listings,
                this.db.searchSummaries,
                this.db.itemLatest,
                this.db.claimDiagnostics,
                this.db.lineageBranches,
                this.db.processingQueue,
                this.db.repositoryState,
            ], async () => {
                const currentRows = rowsToStateMap(await this.db.repositoryState.bulkGet([
                    PROCESSING_INPUT_GENERATION_KEY,
                    DERIVED_COMMIT_GENERATION_KEY,
                    COMMITTED_MATERIALIZER_EPOCH_KEY,
                ]).then((rows) => rows.filter((row) => row !== undefined)));
                const currentInputGeneration = stateNumber(currentRows, PROCESSING_INPUT_GENERATION_KEY);
                const currentDerivedGeneration = stateNumber(currentRows, DERIVED_COMMIT_GENERATION_KEY);
                const currentEpoch = stateNumber(currentRows, COMMITTED_MATERIALIZER_EPOCH_KEY);
                if (currentInputGeneration !== snapshot.processingInputGeneration
                    || currentDerivedGeneration !== snapshot.derivedCommitGeneration) {
                    throw new StaleSnapshotError();
                }
                if (this.materializerEpoch < currentEpoch)
                    throw new NewerMaterializerError();
                if (currentDerivedGeneration === Number.MAX_SAFE_INTEGER) {
                    throw new Error('The derived commit generation is exhausted.');
                }
                await Promise.all([
                    this.db.searches.clear(),
                    this.db.searchPages.clear(),
                    this.db.listings.clear(),
                    this.db.searchSummaries.clear(),
                    this.db.itemLatest.clear(),
                    this.db.claimDiagnostics.clear(),
                    this.db.lineageBranches.clear(),
                ]);
                await Promise.all([
                    this.db.searches.bulkAdd([...processed.searches]),
                    this.db.searchPages.bulkAdd([...processed.pages]),
                    this.db.listings.bulkAdd([...processed.listings]),
                    this.db.searchSummaries.bulkAdd([...processed.summaries]),
                    this.db.itemLatest.bulkAdd([...processed.latestItems]),
                    this.db.claimDiagnostics.bulkAdd([...processed.claimDiagnostics]),
                    this.db.lineageBranches.bulkAdd([...processed.lineageBranches]),
                ]);
                await this.db.processingQueue.bulkDelete([...snapshot.queueKeys]);
                await this.db.repositoryState.bulkPut([
                    { key: COMMITTED_PROCESSOR_VERSION_KEY, value: this.processor.version },
                    { key: COMMITTED_STATISTICS_VERSION_KEY, value: this.processor.statisticsVersion },
                    { key: COMMITTED_MARKET_VARIANT_KEY, value: this.processor.marketVariant },
                    { key: COMMITTED_MATERIALIZER_EPOCH_KEY, value: this.materializerEpoch },
                    {
                        key: REPRESENTED_PROCESSING_INPUT_GENERATION_KEY,
                        value: snapshot.processingInputGeneration,
                    },
                    { key: DERIVED_COMMIT_GENERATION_KEY, value: currentDerivedGeneration + 1 },
                ]);
            });
        }
        catch (error) {
            if (error instanceof StaleSnapshotError)
                return emptyProcessResult('stale-snapshot');
            if (error instanceof NewerMaterializerError)
                return emptyProcessResult('newer-materializer');
            throw repositoryError(error, 'Committing local market data');
        }
        this.notify();
        return {
            disposition: 'committed',
            processedQueueCount: snapshot.queueKeys.length,
            searchCount: processed.searches.length,
            listingCount: processed.listings.length,
            unassignedRevisionIds: processed.unassignedRevisionIds,
            ambiguousRevisionIds: processed.ambiguousRevisionIds,
        };
    }
    serializeProcessing(run) {
        const result = this.processingTail.then(run, run);
        this.processingTail = result.then(() => undefined, () => undefined);
        return result;
    }
    notify() {
        for (const listener of this.listeners)
            listener();
    }
}
export function createFredrickLocalRepository(databaseName = FREDRICK_LOCAL_DATABASE_NAME) {
    return new DexieFredrickLocalRepository(databaseName);
}
